import { useEffect, useRef, useState, type ReactElement } from 'react';
import { CalendarClock, X } from 'lucide-react';
import {
  DUE_REMINDER_DEFAULT_MINUTES_BEFORE,
  type CardDetail,
  type DueReminderOffsetMinutes,
} from '@molt/shared';
import { Button, IconButton, Notice, Popover, TextField } from '@/components';
import {
  dueStatus,
  dueStatusLabel,
  REMINDER_OPTIONS,
  reminderLabel,
} from '@/lib/card-dates';
import {
  dateTimeLocalInZoneToIso,
  formatDateTimeLocalInZone,
  formatLocaleCardDate,
} from '@/lib/locale-formatting';
import { useInterfaceLanguage } from '@/lib/interface-language';
import { useLocalePreferencesValue } from '@/lib/locale-preferences';
import {
  useUpdateCardDueDate,
  useUpdateCardDueReminder,
  useUpdateCardStartDate,
} from '@/lib/cards';
import { cn } from '@/lib/cn';

export interface CardDatesSectionProps {
  card: CardDetail;
  boardId: string;
  canEdit: boolean;
  initialOpen?: boolean | undefined;
}

export function CardDatesSection({
  card,
  boardId,
  canEdit,
  initialOpen = false,
}: CardDatesSectionProps): ReactElement {
  const updateStartDate = useUpdateCardStartDate();
  const updateDueDate = useUpdateCardDueDate();
  const updateReminder = useUpdateCardDueReminder();
  const localePreferences = useLocalePreferencesValue();
  const { t } = useInterfaceLanguage();
  const [open, setOpen] = useState(false);
  const [startDraft, setStartDraft] = useState(() =>
    formatDateTimeLocalInZone(card.startAt, localePreferences),
  );
  const [dueDraft, setDueDraft] = useState(() =>
    formatDateTimeLocalInZone(card.dueAt, localePreferences),
  );
  const [reminderDraft, setReminderDraft] = useState<string>(
    card.dueReminderMinutesBefore === null
      ? 'none'
      : String(card.dueReminderMinutesBefore),
  );
  const [error, setError] = useState<string | null>(null);
  const [now, setNow] = useState(() => new Date());
  const initialOpenApplied = useRef(false);
  const title = card.title.trim() === '' ? t('cardBack.untitledCard') : card.title;
  const status = dueStatus({ dueAt: card.dueAt, completed: card.completed, now });
  const hasAnyDate = card.startAt !== null || card.dueAt !== null;
  const dueLabel =
    card.dueAt === null ? null : formatLocaleCardDate(t, card.dueAt, localePreferences);
  const startLabel =
    card.startAt === null
      ? null
      : formatLocaleCardDate(t, card.startAt, localePreferences);
  const inverted =
    startDraft !== '' &&
    dueDraft !== '' &&
    new Date(startDraft).getTime() > new Date(dueDraft).getTime();

  useEffect(() => {
    if (initialOpenApplied.current || !initialOpen || !canEdit) return;
    initialOpenApplied.current = true;
    setOpen(true);
  }, [canEdit, initialOpen]);

  useEffect(() => {
    if (!canEdit) return undefined;

    function handleKeyDown(event: KeyboardEvent) {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (event.key.toLowerCase() !== 'd') return;
      if (isTextEntryTarget(event.target)) return;
      // A character typed in an open menu (the card back's Card actions, a
      // comment's actions) is that menu's typeahead, and Radix does not stop
      // its propagation: without this, "d" on a menuitem opened Dates under
      // the modal menu, which then dismissed it as an outside focus.
      if (isInsideMenu(event.target)) return;
      event.preventDefault();
      setOpen(true);
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [canEdit]);

  useEffect(() => {
    if (!open) return;
    setStartDraft(formatDateTimeLocalInZone(card.startAt, localePreferences));
    setDueDraft(formatDateTimeLocalInZone(card.dueAt, localePreferences));
    setReminderDraft(
      card.dueReminderMinutesBefore === null
        ? 'none'
        : String(card.dueReminderMinutesBefore),
    );
    setError(null);
  }, [card.dueAt, card.dueReminderMinutesBefore, card.startAt, localePreferences, open]);

  useEffect(() => {
    if (card.dueAt === null || card.completed) return undefined;
    const interval = window.setInterval(() => {
      setNow(new Date());
    }, 60_000);
    return () => window.clearInterval(interval);
  }, [card.completed, card.dueAt]);

  async function saveDates() {
    setError(null);
    try {
      // ⚠ The thrown message from either call is rendered verbatim by the catch
      // below (`cause.message`), which is why `dateTimeLocalInZoneToIso` routes
      // its throw rather than staying English as `lib/card-dates.ts`'s
      // byte-identical sibling does.
      const startAt =
        startDraft === ''
          ? null
          : dateTimeLocalInZoneToIso(t, startDraft, localePreferences);
      const dueAt =
        dueDraft === '' ? null : dateTimeLocalInZoneToIso(t, dueDraft, localePreferences);
      if (startAt !== card.startAt) {
        await updateStartDate.mutateAsync({ cardId: card.id, boardId, startAt });
      }
      if (dueAt !== card.dueAt) {
        await updateDueDate.mutateAsync({ cardId: card.id, boardId, dueAt });
      }
      if (dueAt !== null) {
        const dueReminderMinutesBefore =
          reminderDraft === 'none'
            ? null
            : (Number(reminderDraft) as DueReminderOffsetMinutes);
        if (dueReminderMinutesBefore !== card.dueReminderMinutesBefore) {
          await updateReminder.mutateAsync({
            cardId: card.id,
            boardId,
            dueReminderMinutesBefore,
          });
        }
      }
      setOpen(false);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t('cardBack.dates.saveFailed'));
    }
  }

  async function removeDates() {
    setError(null);
    try {
      if (card.startAt !== null) {
        await updateStartDate.mutateAsync({ cardId: card.id, boardId, startAt: null });
      }
      if (card.dueAt !== null) {
        await updateDueDate.mutateAsync({ cardId: card.id, boardId, dueAt: null });
      }
      setOpen(false);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t('cardBack.dates.removeFailed'));
    }
  }

  return (
    <section aria-labelledby="card-dates-heading" className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <h2 id="card-dates-heading" className="text-sm font-semibold text-ink">
          {t('cardBack.dates.heading')}
        </h2>
        <div className="min-w-0 flex-1" />
        {status !== 'none' ? (
          <span
            className={cn(
              'inline-flex items-center gap-1 rounded-card border bg-surface-raised px-2 py-1 text-xs font-medium',
              status === 'complete' && 'border-ok text-ok',
              status === 'due-soon' && 'border-warn text-warn',
              (status === 'overdue' || status === 'long-overdue') &&
                'border-bad text-bad',
              status === 'neutral' && 'border-border-strong text-ink-muted',
            )}
          >
            <CalendarClock aria-hidden="true" className="size-3.5" />
            {dueLabel === null
              ? dueStatusLabel(t, status)
              : t('cardBack.dates.chip', {
                  date: dueLabel,
                  status: dueStatusLabel(t, status),
                })}
          </span>
        ) : null}
        {canEdit ? (
          <Popover
            open={open}
            onOpenChange={setOpen}
            title={t('cardBack.dates.popoverTitle')}
            width={340}
            trigger={
              <Button
                type="button"
                size="sm"
                variant="secondary"
                aria-label={t('cardBack.dates.trigger', { title })}
                iconStart={<CalendarClock aria-hidden="true" className="size-4" />}
              >
                {t('cardBack.dates.heading')}
              </Button>
            }
            closeControl={
              <IconButton label={t('cardBack.dates.closePopover')} size="sm">
                <X aria-hidden="true" className="size-4" />
              </IconButton>
            }
            footer={
              <div className="flex w-full flex-wrap justify-between gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="quiet"
                  aria-label={t('cardBack.dates.removeLabel')}
                  disabled={!hasAnyDate}
                  onClick={() => void removeDates()}
                >
                  {t('cardBack.dates.remove')}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="primary"
                  busy={
                    updateStartDate.isPending ||
                    updateDueDate.isPending ||
                    updateReminder.isPending
                  }
                  busyLabel={t('cardBack.dates.saving')}
                  onClick={() => void saveDates()}
                >
                  {t('cardBack.dates.save')}
                </Button>
              </div>
            }
          >
            <div className="flex flex-col gap-3">
              <TextField
                type="datetime-local"
                label={t('cardBack.dates.startField')}
                value={startDraft}
                onChange={(event) => setStartDraft(event.currentTarget.value)}
              />
              <TextField
                type="datetime-local"
                label={t('cardBack.dates.dueField')}
                value={dueDraft}
                onChange={(event) => {
                  const next = event.currentTarget.value;
                  setDueDraft(next);
                  if (
                    next !== '' &&
                    dueDraft === '' &&
                    card.dueReminderMinutesBefore === null
                  ) {
                    setReminderDraft(String(DUE_REMINDER_DEFAULT_MINUTES_BEFORE));
                  }
                }}
              />
              <label className="flex flex-col gap-1 text-sm font-medium text-ink">
                {t('cardBack.dates.reminderField')}
                <select
                  className="h-9 rounded-card border border-border-strong bg-surface-raised px-3 text-sm text-ink focus:border-accent disabled:cursor-not-allowed disabled:text-ink-muted"
                  value={dueDraft === '' ? 'none' : reminderDraft}
                  disabled={dueDraft === ''}
                  onChange={(event) => setReminderDraft(event.currentTarget.value)}
                >
                  {REMINDER_OPTIONS.map((offset) => (
                    <option key={offset ?? 'none'} value={offset ?? 'none'}>
                      {reminderLabel(t, offset)}
                    </option>
                  ))}
                </select>
              </label>
              {inverted ? (
                <Notice
                  tone="warn"
                  title={t('cardBack.dates.invertedTitle')}
                  body={t('cardBack.dates.invertedBody')}
                />
              ) : null}
              {error !== null ? (
                <Notice tone="bad" title={error} body={t('cardBack.dates.errorBody')} />
              ) : null}
            </div>
          </Popover>
        ) : null}
      </div>

      <div className="flex flex-wrap gap-2 text-sm text-ink-muted">
        {startLabel !== null ? (
          <span>{t('cardBack.dates.startsAt', { date: startLabel })}</span>
        ) : null}
        {dueLabel !== null ? (
          <span>
            {t('cardBack.dates.dueWithStatus', {
              date: dueLabel,
              status: dueStatusLabel(t, status),
            })}
          </span>
        ) : card.completed ? (
          <span>{dueStatusLabel(t, status)}</span>
        ) : (
          <span>{t('cardBack.dates.noDates')}</span>
        )}
        {dueLabel !== null ? (
          <span>
            {t('cardBack.dates.reminderSummary', {
              reminder: reminderLabel(t, card.dueReminderMinutesBefore),
            })}
          </span>
        ) : null}
      </div>
    </section>
  );
}

function isInsideMenu(target: EventTarget | null): boolean {
  return target instanceof Element && target.closest('[role="menu"]') !== null;
}

function isTextEntryTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
}
